import { useState, useEffect, useCallback } from 'react';

import type { PagePermissionResponse, PageContentResponse, IncludePageStateMessage } from '../types/messages';

import { createConversationId } from '@/lib/api';

interface UseIncludePageProps {
  isAgentAssist?: boolean;
  apiKey: string;
  trackEvent?: (eventName: string, data: Record<string, any>) => void;
}

/**
 * Hook to manage "include page" toggle functionality for agent assist mode.
 * Handles permission requests, page content fetching, and state management.
 */
export const useIncludePage = ({ isAgentAssist, apiKey, trackEvent }: UseIncludePageProps) => {
  const [includePageEnabled, setIncludePageEnabled] = useState(false);
  const [includePageLoading, setIncludePageLoading] = useState(false);
  const [includePageDisabled, setIncludePageDisabled] = useState(false);

  /**
   * Sends a message to the parent window and waits for a response with matching requestId
   */
  const sendMessageToParent = useCallback(
    <T>(message: object, timeout: number = 5000): Promise<T> => {
      return new Promise((resolve, reject) => {
        const requestId = createConversationId();
        /* eslint-disable-next-line prefer-const */
        let timeoutId: ReturnType<typeof setTimeout>;

        const handler = (event: MessageEvent) => {
          if (event.data.requestId === requestId) {
            clearTimeout(timeoutId);
            window.removeEventListener('message', handler);
            resolve(event.data as T);
          }
        };

        timeoutId = setTimeout(() => {
          window.removeEventListener('message', handler);
          reject(new Error('Request timeout'));
        }, timeout);

        window.addEventListener('message', handler);
        window.parent.postMessage({ ...message, requestId, widgetKey: apiKey }, '*');
      });
    },
    [apiKey],
  );

  /**
   * Handles toggling the include page feature on/off
   */
  const handleIncludePageToggle = useCallback(
    async (enabled: boolean) => {
      setIncludePageLoading(true);

      try {
        if (enabled) {
          // Request permissions immediately when turning ON
          const response = await sendMessageToParent<PagePermissionResponse>({
            type: 'REQUEST_PAGE_PERMISSION',
          });

          if (!response.granted) {
            trackEvent?.('Include Page Permission Denied', {});
            throw new Error('Permission denied');
          }

          // Notify parent to update storage when turning ON
          window.parent.postMessage(
            {
              type: 'UPDATE_PAGE_TOGGLE_STATE',
              enabled: true,
              widgetKey: apiKey,
            },
            '*',
          );

          trackEvent?.('Include Page Enabled', {});
          setIncludePageEnabled(true);
        } else {
          // Notify parent to update storage when turning OFF
          window.parent.postMessage(
            {
              type: 'UPDATE_PAGE_TOGGLE_STATE',
              enabled: false,
              widgetKey: apiKey,
            },
            '*',
          );

          trackEvent?.('Include Page Disabled', {});
          setIncludePageEnabled(false);
        }
      } catch (error) {
        console.error('Failed to toggle include page:', error);
        setIncludePageEnabled(false);
      } finally {
        setIncludePageLoading(false);
      }
    },
    [apiKey, sendMessageToParent, trackEvent],
  );

  /**
   * Fetches page content from the Chrome extension
   */
  const fetchPageContext = useCallback(
    async (conversationId?: string, searchQuery?: string) => {
      if (!includePageEnabled || !isAgentAssist || includePageDisabled) {
        return undefined;
      }

      try {
        setIncludePageLoading(true);
        const response = await sendMessageToParent<PageContentResponse>(
          {
            type: 'REQUEST_PAGE_CONTENT',
          },
          5000,
        );

        if (response.success && response.content) {
          trackEvent?.('Page Content Included', {
            conversationId,
            searchQuery,
            contentLength: response.content.length,
          });

          return [
            {
              type: 'text' as const,
              text: `I am currently viewing this web page:\n\n${response.content}`,
            },
          ];
        }

        return undefined;
      } catch (error) {
        console.error('Failed to fetch page content:', error);
        trackEvent?.('Page Content Fetch Failed', { conversationId, searchQuery });

        return undefined;
      } finally {
        setIncludePageLoading(false);
      }
    },
    [includePageEnabled, isAgentAssist, includePageDisabled, sendMessageToParent, trackEvent],
  );

  // Listen for include page state changes from parent (for tab switching)
  useEffect(() => {
    if (!isAgentAssist) return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as IncludePageStateMessage;
      if (data.type === 'INCLUDE_PAGE_STATE') {
        setIncludePageEnabled(data.enabled || false);
        setIncludePageDisabled(data.disabled || false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [isAgentAssist]);

  return {
    includePageEnabled,
    includePageLoading,
    includePageDisabled,
    handleIncludePageToggle,
    fetchPageContext,
  };
};
